import React, { MouseEvent, PureComponent, ReactElement } from 'react';

import uuid from 'uuid/v4';

import { InformationBarAllergiesList, InformationBarAllergy, InformationBarAPI, InformationBarField } from '@philips-emr/tws-components-api';
import { Alert, Breakpoint, Expression } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { eventListener, expression, object, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { CollapseEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, isFunction } from '@philips-emr/tws-utils';

import { Field, Text } from '..';
import { ReactComponent as Allergies } from '../../../resources/assets/components/DLS_Alert.svg';
import { ReactComponent as Birthday } from '../../../resources/assets/components/DLS_Birthday.svg';
import { ReactComponent as FallRisk } from '../../../resources/assets/components/DLS_Fall_risk.svg';
import { ReactComponent as Isolation } from '../../../resources/assets/components/DLS_Isolation.svg';
import { ReactComponent as Preventive } from '../../../resources/assets/components/DLS_Preventive.svg';
import { ReactComponent as VIP } from '../../../resources/assets/components/DLS_VIP.svg';
import withRouterContext, { RouterContextProps } from '../with-router-context/with-router-context';

import { InformationBarExpander, InformationBarSecondaryRow } from './components';
import './information-bar.css';

export interface InformationBarProps {
  alerts?: Record<Alert, string>;
  alertsTitle?: string;
  allergies?: InformationBarAllergiesList;
  extraInformation?: Record<string, InformationBarField>;
  extraInformationTitle?: string;
  fieldOneTitle?: string;
  fieldOneValue?: string;
  fieldTwoTitle?: string;
  fieldTwoValue?: string;
  image: string;
  onCollapse?: (event: CollapseEvent) => void;
  title: string;
}

interface InformationBarState {
  alertsCollapsed: boolean;
  collapsed: boolean;
}

@AwesomeComponent({ type: ComponentType.DATA_DISPLAY, api: InformationBarAPI })
class InformationBar extends PureComponent<InformationBarProps, InformationBarState> {
  state: InformationBarState = {
    alertsCollapsed: true,
    collapsed: true,
  };

  static propTypes = {
    alerts: object.notEnumerable(),
    alertsTitle: string,
    allergies: object,
    extraInformation: object.notEnumerable(),
    extraInformationTitle: string,
    fieldOneTitle: string,
    fieldOneValue: string,
    fieldTwoTitle: string,
    fieldTwoValue: string,
    image: string,
    onCollapse: eventListener,
    title: expression,
  };

  static defaultProps = {
    alerts: {},
    alertsTitle: Expression.ALERTS,
    fieldOneTitle: '',
    fieldTwoTitle: '',
    extraInformationTitle: Expression.PERSONAL_INFORMATION,
    title: '',
  };

  ICONS_MAP: Map<Alert, () => ReactElement> = new Map([
    [Alert.ALLERGIES, () => <Allergies className="c-information-bar__icon-alert" key={Alert.ALLERGIES} />],
    [Alert.BIRTHDAY, () => <Birthday className="c-information-bar__icon-birthday" key={Alert.BIRTHDAY} />],
    [Alert.FALL_RISK, () => <FallRisk className="c-information-bar__icon-alert" key={Alert.FALL_RISK} />],
    [Alert.ISOLATION, () => <Isolation key={Alert.ISOLATION} />],
    [Alert.PREVENTIVE_MEDICINE, () => <Preventive className="c-information-bar__icon-preventive" key={Alert.PREVENTIVE_MEDICINE} />],
    [Alert.VIP, () => <VIP className="c-information-bar__icon-vip" key={Alert.VIP} />],
  ]);

  getIcon(alert: Alert): ReactElement | null {
    const iconFunction = this.ICONS_MAP.get(alert);

    if (isFunction(iconFunction)) {
      return iconFunction();
    }

    return null;
  }

  toggleFullView = (): void => {
    const { onCollapse } = this.props;

    const triggerOnCollapse = (): void => {
      const collapsed = this.state.collapsed;

      isFunction(onCollapse) && onCollapse(new CollapseEvent({ collapsed }));
    };

    this.setState((prevState) => ({
      collapsed: !prevState.collapsed,
    }), triggerOnCollapse);
  };

  toggleAlerts = (event: MouseEvent<HTMLDivElement>): void => {
    this.setState((prevState) => ({ alertsCollapsed: !prevState.alertsCollapsed }));

    event.stopPropagation();
  };

  getFirstAlertIcon(): ReactElement | null {
    const { alerts, allergies } = this.props;

    if (allergies) {
      return this.getIcon(Alert.ALLERGIES);
    }

    if (!alerts) {
      return null;
    }

    const keys = Object.keys(alerts) as Alert[];
    const alert = keys.length > 0 ? keys[0] : null;
    return alert ? this.getIcon(alert) : null;
  }

  renderHeaderInfo(): ReactElement {
    const { fieldOneTitle = '', fieldOneValue = '', fieldTwoTitle = '', fieldTwoValue = '', title } = this.props;

    const { collapsed } = this.state;

    const headerClassName = classNames({
      'c-information-bar__header-fields': true,
      'is-collapsed': collapsed,
    });

    const classNameField1 = {
      'c-information-bar__header-field': true,
      'c-text': true,
      [`c-text--${Breakpoint.SM}`]: true,
    };

    const classNameField2 = classNames({
      ...classNameField1,
      'u-text-right': collapsed,
    });

    return (
      <span className="c-information-bar__header-info">
        <div className="c-information-bar__header-title">
          <Text className="u-col-12 c-information-bar__data-text u-truncate">{title}</Text>
          <div>
            {collapsed ? this.getFirstAlertIcon() : null}
          </div>
        </div>
        <div className={headerClassName}>
          <div className={classNames(classNameField1)}>
            <label className="c-information-bar__label-text">{`${msg(fieldOneTitle)} `}</label>
            <span className="c-information-bar__data-text u-truncate">{msg(fieldOneValue)}</span>
          </div>

          <div className={classNameField2}>
            <label className="c-information-bar__label-text">{`${msg(fieldTwoTitle)} `}</label>
            <span className="c-information-bar__data-text u-truncate">{msg(fieldTwoValue)}</span>
          </div>
        </div>
      </span>
    );
  }

  renderImage(): ReactElement | null {
    const { image } = this.props;

    if (!image) {
      return null;
    }

    const imageComponent = ({ href }: RouterContextProps): ReactElement => {
      return <div className="c-information-bar__avatar-img" style={{ backgroundImage: `url(${href})` }} x-contrast="bd" />;
    };

    const ImageElement = withRouterContext(imageComponent, image);

    return <ImageElement />;
  }

  renderData(): ReactElement {
    return (
      <div className="c-information-bar__avatar-data">
        {this.renderHeaderInfo()}
      </div>
    );
  }

  renderHeader(): ReactElement {
    return (
      <div className="c-information-bar__avatar">
        {this.renderImage()}
        {this.renderData()}
      </div>
    );
  }

  renderBody(): ReactElement {
    return (
      <React.Fragment>
        {this.renderAlerts()}
        {this.renderExtraInformation()}
      </React.Fragment>
    );
  }

  renderAlertsList(): ReactElement[] | null {
    const { alerts, allergies } = this.props;
    const { alertsCollapsed: renderOnlyFirstTwo } = this.state;

    let countAlerts = 0;
    const alertsArray: ReactElement[] = [];

    if (allergies) {
      countAlerts++;
      alertsArray.push(this.renderAllergies(allergies));
    }

    if (!alerts) {
      return alertsArray;
    }

    Object.entries(alerts).every(([alert, description]) => {
      if (countAlerts > 1 && renderOnlyFirstTwo) {
        return false;
      }

      countAlerts++;
      alertsArray.push(this.renderAlertRow(alert as Alert, description));
      return true;
    });

    return (
      alertsArray
    );
  }

  renderAlerts(): ReactElement | null {
    const { alerts = {}, alertsTitle = Expression.ALERTS, allergies } = this.props;
    const { alertsCollapsed } = this.state;

    const countAlerts = Object.keys(alerts).length + (allergies ? 1 : 0);

    if (countAlerts === 0) {
      return null;
    }

    const shouldRenderExpander = countAlerts > 2 || allergies;

    return (
      <InformationBarSecondaryRow onClick={this.toggleAlerts}>
        <div className="c-information-bar__list-title">
          <Text className="c-information-bar__data-text u-truncate u-col-12">{msg(alertsTitle)}</Text>
          {shouldRenderExpander ? <InformationBarExpander expanded={alertsCollapsed} /> : null}
        </div>
        <div className="c-information-bar__list">
          {this.renderAlertsList()}
        </div>
      </InformationBarSecondaryRow>
    );
  }

  renderAllergies(allergies: InformationBarAllergiesList): ReactElement {
    const { alertsCollapsed } = this.state;

    const allergiesCount = allergies.allergies.length;

    return (
      <div className="c-information-bar__list-item" key={Alert.ALLERGIES}>
        <div className="c-information-bar__list">
          <div>
            {this.getIcon(Alert.ALLERGIES)}
          </div>
          <Text className="c-information-bar__list-item-text c-information-bar__data-text">
            {`${msg(allergies.title || Expression.ALLERGIES)} (${allergiesCount})`}
          </Text>
          <div className="c-information-bar__list c-information-bar__list-item c-information-bar__allergies-list">
            {alertsCollapsed ? null : allergies.allergies.map((alergy: InformationBarAllergy) => this.renderAllergiesDetails(alergy))}
          </div>
        </div>
      </div>
    );
  }

  renderAllergiesDetails(alergy: InformationBarAllergy): ReactElement {
    return (
      <div className="c-information-bar__list-item" key={uuid()}>
        <Field label={msg(alergy.severity)} className="c-information-bar__label-text" width={12}>
          <Text className="c-information-bar__data-text">{`${msg(alergy.description)}`}</Text>
        </Field>
      </div>
    );
  }

  renderAlertRow(alert: Alert, description: string): ReactElement {
    return (
      <div className="c-information-bar__list-item" key={alert}>
        <div>
          {this.getIcon(alert)}
        </div>
        <Text className="c-information-bar__list-item-text c-information-bar__data-text">{msg(description)}</Text>
      </div>
    );
  }

  renderExtraInformation(): ReactElement | null {
    const { extraInformation, extraInformationTitle = Expression.PERSONAL_INFORMATION } = this.props;

    if (!extraInformation) {
      return null;
    }

    const visibleInformations = Object.values(extraInformation).filter(info => info.visible);

    if (visibleInformations.length === 0) {
      return null;
    }

    return (
      <InformationBarSecondaryRow>
        <div className="c-information-bar__list-title">
          <Text className="c-information-bar__data-text u-truncate u-col-12">{msg(extraInformationTitle)}</Text>
        </div>
        <div className="c-information-bar__list">
          {visibleInformations.map((info) => this.renderExtraInformationRow(info))}
        </div>
      </InformationBarSecondaryRow>
    );
  }

  renderExtraInformationRow(info: InformationBarField): ReactElement {
    return (
      <div className="c-information-bar__list-item" key={info.label}>
        <Field label={msg(info.label)} className="c-information-bar__label-text" width={12}>
          <Text className="c-information-bar__data-text">{`${msg(info.value || Expression.NA)}`}</Text>
        </Field>
      </div>
    );
  }

  renderComplete(): ReactElement {
    return (
      <React.Fragment>
        {this.renderHeader()}
        {this.renderBody()}
      </React.Fragment>
    );
  }

  render(): ReactElement {
    const { collapsed } = this.state;

    return (
      <div className="c-information-bar" onClick={this.toggleFullView}>
        <div className="c-information-bar__row c-information-bar__row--primary">
          {collapsed ? this.renderHeaderInfo() : this.renderComplete()}
        </div>
      </div>
    );
  }
}

export default InformationBar;
